/**
 * UsernamePromptModal — shown after a NEW social sign-in (no existing account
 * for that Apple/Google identity). Asks the player to pick a handle, with their
 * assigned default name (e.g. "Player1234") as placeholder text so they can
 * just hit confirm to keep it.
 *
 * Body-level singleton. Emits 'submit' with the chosen name (or the default
 * when left blank).
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

interface UsernamePromptEvents {
  /** Confirmed — `name` is the entered handle, or the default if left blank. */
  'submit': { name: string };
  /** Fires on every keystroke with the raw input value — drives the live
   *  availability check (wired in main.ts via DebouncedUsernameChecker). */
  'input': { name: string };
}

export type UsernameAvailability = 'idle' | 'checking' | 'available' | 'taken';

const STYLE_ID = 'sdl-uname-styles';
const MAX_LEN = 16;

export class UsernamePromptModal extends EventEmitter<UsernamePromptEvents> {
  public element: HTMLElement;
  private inputEl: HTMLInputElement;
  private statusEl: HTMLElement;
  private btnEl: HTMLButtonElement;
  private defaultName = '';
  private visible = false;
  private availability: UsernameAvailability = 'idle';

  constructor() {
    super();
    UsernamePromptModal.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-uname-host';
    this.element.innerHTML = `
      <div class="sdl-uname-backdrop"></div>
      <div class="sdl-uname-panel">
        <div class="sdl-uname-kicker">YOU'RE IN</div>
        <div class="sdl-uname-title">PICK YOUR NAME</div>
        <div class="sdl-uname-sub">This is how the block will know you.</div>
        <input class="sdl-uname-input" type="text" maxlength="${MAX_LEN}" autocomplete="off"
          autocapitalize="off" autocorrect="off" spellcheck="false" />
        <div class="sdl-uname-status"></div>
        <button class="sdl-uname-btn" data-confirm>LET'S GO</button>
      </div>
    `;
    document.body.appendChild(this.element);

    this.inputEl = this.element.querySelector('.sdl-uname-input') as HTMLInputElement;
    this.statusEl = this.element.querySelector('.sdl-uname-status') as HTMLElement;
    this.btnEl = this.element.querySelector('[data-confirm]') as HTMLButtonElement;

    this.btnEl.addEventListener('click', () => this.confirm());
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.confirm();
    });
    this.inputEl.addEventListener('input', () => {
      this.emit('input', { name: this.inputEl.value });
    });
  }

  /** Show the prompt with the server-assigned default as placeholder. */
  show(defaultName: string): void {
    this.defaultName = defaultName;
    this.inputEl.value = '';
    this.inputEl.placeholder = defaultName;
    this.setAvailability('idle');
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
    // Focus after the show transition so the keyboard rises cleanly on mobile.
    window.setTimeout(() => this.inputEl.focus(), 350);
  }

  /** The name the prompt falls back to when the input is left blank. */
  getDefaultName(): string {
    return this.defaultName;
  }

  /**
   * Reflect the live availability check in the UI. 'taken' blocks confirm;
   * 'checking' does NOT (the server re-validates on submit anyway — never
   * dead-button a fast typer over a 300ms debounce).
   */
  setAvailability(state: UsernameAvailability): void {
    this.availability = state;
    this.statusEl.className = `sdl-uname-status ${state}`;
    this.statusEl.textContent =
      state === 'checking' ? 'CHECKING…'
      : state === 'available' ? 'AVAILABLE'
      : state === 'taken' ? 'TAKEN, TRY ANOTHER'
      : '';
    this.btnEl.disabled = state === 'taken';
  }

  /** Re-prompt after the server rejected a submitted name as taken. */
  showTaken(name: string): void {
    this.inputEl.value = name;
    this.setAvailability('taken');
    window.setTimeout(() => this.inputEl.focus(), 350);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
    this.inputEl.blur();
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private confirm(): void {
    if (this.availability === 'taken') return; // blocked — pick another name
    const typed = this.inputEl.value.trim();
    const name = typed.length > 0 ? typed : this.defaultName;
    this.emit('submit', { name });
    this.hide();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-uname-host {
        position: fixed; inset: 0;
        z-index: 860;
        display: none;
        align-items: center; justify-content: center;
      }
      .sdl-uname-host.visible { display: flex; }
      .sdl-uname-backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.8);
        backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
      }
      .sdl-uname-panel {
        position: relative;
        width: 300px;
        padding: 28px 24px 20px;
        background: var(--concrete, #111114);
        border: 1px solid var(--glass-border, rgba(255,255,255,0.08));
        border-radius: 22px;
        display: flex; flex-direction: column; align-items: center;
        gap: 8px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.65);
        animation: sdl-uname-in 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      @keyframes sdl-uname-in {
        0% { opacity: 0; transform: translateY(-30px) scale(0.8); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      .sdl-uname-kicker {
        font-family: 'Rajdhani', sans-serif;
        font-size: 12px; letter-spacing: 4px;
        color: rgba(255,255,255,0.45);
      }
      .sdl-uname-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 32px; letter-spacing: 1px;
        color: #fff;
      }
      .sdl-uname-sub {
        font-family: 'Rajdhani', sans-serif;
        font-size: 13px; text-align: center;
        color: rgba(255,255,255,0.55);
        margin-bottom: 8px;
      }
      .sdl-uname-input {
        width: 100%;
        padding: 13px 14px;
        background: rgba(255,255,255,0.06);
        border: 1px solid rgba(0,229,255,0.3);
        border-radius: 12px;
        color: #fff;
        font-family: 'Rajdhani', sans-serif;
        font-size: 18px; font-weight: 600; text-align: center;
        outline: none;
      }
      .sdl-uname-input::placeholder { color: rgba(255,255,255,0.35); font-weight: 500; }
      .sdl-uname-input:focus { border-color: rgba(0,229,255,0.7); }
      .sdl-uname-status {
        min-height: 16px;
        font-family: 'Rajdhani', sans-serif;
        font-size: 12px; letter-spacing: 1.5px; font-weight: 600;
        text-align: center;
      }
      .sdl-uname-status.checking { color: rgba(255,255,255,0.72); }
      .sdl-uname-status.available { color: #00FF88; }
      .sdl-uname-status.taken { color: #FF5A5A; }
      .sdl-uname-btn:disabled {
        opacity: 0.45;
        filter: saturate(0.4);
        cursor: default;
      }
      .sdl-uname-btn {
        width: 100%;
        margin-top: 10px;
        padding: 14px 0;
        border: none; border-radius: 14px;
        background: linear-gradient(135deg, #FFD75E, #F5A623);
        color: #241a04;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 22px; letter-spacing: 2px;
        cursor: pointer;
        box-shadow: 0 6px 20px rgba(245,166,35,0.3);
        transition: transform 0.1s;
      }
      .sdl-uname-btn:active { transform: scale(0.97); }
    `;
    document.head.appendChild(style);
  }
}
